import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowUp, ArrowDown, ChevronRight, Plus, Trash2,
  Save, Eye, CheckCircle2, AlertTriangle, X, Loader2, RefreshCw,
  FileText, HelpCircle, Package, ListChecks, Link2, Info, Sparkles,
} from "lucide-react";
import { PageHeader, DataCard, StatusPill } from "@/components/admin/AdminPrimitives";
import {
  getTemplateTree, updateTemplate, setTemplatePublished,
  upsertStage, deleteStage, moveStage,
  upsertMission, deleteMission, moveMission,
  getMissionEditor, validateMission,
  upsertSlide, deleteSlide, moveSlide,
  upsertQuestion, deleteQuestion, moveQuestion,
  upsertDeliverable, deleteDeliverable,
  upsertExternalTask, deleteExternalTask, moveExternalTask,
  upsertResource, deleteResource, moveResource,
  reorderEntities,
  WORKSPACE_FIELDS,
} from "@/lib/admin-journeys.functions";
import { SortableList, SortableRow } from "@/components/admin/Sortable";
import {
  JourneyVersionBar, DraftGate, PublishModal, HistoryPanel, SnapshotViewer,
  useVersionState, type VersionView,
} from "@/components/admin/JourneyVersionBar";
import { JourneyMetricsPanel } from "@/components/admin/JourneyMetricsPanel";

type SearchParams = { m?: string };

export const Route = createFileRoute("/_authenticated/admin/conteudos/jornadas/$templateId")({
  head: () => ({
    meta: [
      { title: "Editor de jornada — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>): SearchParams => ({
    m: typeof s.m === "string" ? s.m : undefined,
  }),
  component: TemplateEditorPage,
});

type Tab = "info" | "slides" | "questions" | "deliverable" | "externals" | "resources";

function TemplateEditorPage() {
  const { templateId } = Route.useParams();
  const { m: selectedMissionId } = Route.useSearch();
  const nav = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();

  const loadTree = useServerFn(getTemplateTree);
  const tree = useQuery({
    queryKey: ["admin.jornada.tree", templateId],
    queryFn: () => loadTree({ data: { templateId } }),
  });

  const versionQ = useVersionState(templateId);
  const versions = versionQ.data?.versions ?? [];
  const draftVersion = versions.find((v: any) => v.status === "draft");
  const publishedVersion = versions.find((v: any) => v.status === "published");
  const hasDraft = !!draftVersion;

  const [tab, setTab] = useState<Tab>("info");
  const [view, setView] = useState<VersionView>("draft");
  const [publishOpen, setPublishOpen] = useState(false);

  const selectMission = useCallback((id?: string) => {
    nav({ search: (prev: SearchParams) => ({ ...prev, m: id }) } as never);
    setTab("info");
  }, [nav]);

  return (
    <div>
      <PageHeader
        title={tree.data?.template?.title ?? "Editor de jornada"}
        description={
          tree.data?.business
            ? `Jornada do negócio: ${tree.data.business.title}`
            : "Jornada"
        }
        back={{ to: "/admin/conteudos/jornadas", label: "Voltar para jornadas" }}
      />

      <JourneyVersionBar
        templateId={templateId}
        view={view}
        setView={setView}
        onOpenPublish={() => setPublishOpen(true)}
      />

      {publishOpen && (
        <PublishModal
          templateId={templateId}
          onClose={() => setPublishOpen(false)}
          onPublished={() => { setPublishOpen(false); setView("published"); }}
        />
      )}

      {view === "history" && <HistoryPanel templateId={templateId} />}
      {view === "metrics" && <JourneyMetricsPanel templateId={templateId} />}
      {view === "published" && (
        publishedVersion
          ? <SnapshotViewer versionId={publishedVersion.id} />
          : <div className="rounded-2xl border border-dashed border-black/10 bg-white p-8 text-center text-sm text-[var(--gray-ink)]">Nenhuma versão publicada ainda.</div>
      )}
      {view === "draft" && (
        <DraftGate templateId={templateId} hasDraft={hasDraft} view={view}>
          <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
            <TreeSidebar
              tree={tree.data}
              isLoading={tree.isLoading}
              selectedMissionId={selectedMissionId}
              onSelectMission={selectMission}
              templateId={templateId}
            />
            <div className="min-w-0">
              {selectedMissionId ? (
                <MissionEditorPanel
                  missionId={selectedMissionId}
                  tab={tab}
                  setTab={setTab}
                  onDeleted={() => selectMission(undefined)}
                  refreshTree={() => qc.invalidateQueries({ queryKey: ["admin.jornada.tree", templateId] })}
                />
              ) : (
                <TemplateInfoPanel template={tree.data?.template} />
              )}
            </div>
          </div>
        </DraftGate>
      )}
    </div>
  );
}

// Header de publicação legacy substituído por <JourneyVersionBar/> (Onda 2.A).
// A flag `journey_templates.published` continua atualizada pela RPC de publicação
// via publish_template_draft(), portanto não precisa mais de UI dedicada aqui.
void setTemplatePublished;

// ==================== TREE SIDEBAR ====================

function TreeSidebar({
  tree, isLoading, selectedMissionId, onSelectMission, templateId,
}: {
  tree: any; isLoading: boolean; selectedMissionId?: string;
  onSelectMission: (id?: string) => void; templateId: string;
}) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin.jornada.tree", templateId] });

  const upStage = useServerFn(upsertStage);
  const delStage = useServerFn(deleteStage);
  const mvStage = useServerFn(moveStage);
  const upMission = useServerFn(upsertMission);
  const delMission = useServerFn(deleteMission);
  const mvMission = useServerFn(moveMission);

  const addStage = useMutation({
    mutationFn: () => upStage({ data: { template_id: templateId, title: "Nova etapa" } }),
    onSuccess: () => { invalidate(); toast.success("Etapa criada."); },
    onError: (e: any) => toast.error(e?.message ?? "Erro"),
  });

  const addMission = useMutation({
    mutationFn: (stageId: string) => upMission({ data: { stage_id: stageId, title: "Nova missão" } }),
    onSuccess: (res: any) => { invalidate(); onSelectMission(res.id); toast.success("Missão criada."); },
    onError: (e: any) => toast.error(e?.message ?? "Erro"),
  });

  const removeStage = async (id: string) => {
    if (!confirm("Arquivar esta etapa? Ela e as missões dela ficam ocultas de novas publicações. Você pode restaurar depois.")) return;
    await delStage({ data: { id } });
    invalidate();
    if (selectedMissionId) onSelectMission(undefined);
    toast.success("Etapa arquivada.");
  };

  const removeMission = async (id: string) => {
    if (!confirm("Arquivar esta missão? Ela some de novas publicações, mas o histórico e projetos existentes continuam intactos.")) return;
    await delMission({ data: { id } });
    invalidate();
    if (selectedMissionId === id) onSelectMission(undefined);
    toast.success("Missão arquivada.");
  };


  return (
    <aside className="rounded-2xl border border-black/[0.06] bg-white p-3 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
      <div className="flex items-center justify-between px-1 pb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Estrutura</h2>
        <button
          onClick={() => addStage.mutate()}
          disabled={addStage.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-[var(--navy)] px-2 py-1 text-[11px] font-semibold text-white hover:opacity-90 disabled:opacity-60"
        >
          <Plus size={11} /> Etapa
        </button>
      </div>

      {isLoading && <div className="p-4 text-xs text-[var(--gray-ink)]">Carregando…</div>}

      {!isLoading && (!tree?.stages || tree.stages.length === 0) && (
        <div className="rounded-lg border border-dashed border-black/10 p-4 text-center text-xs text-[var(--gray-ink)]">
          Adicione a primeira etapa para começar.
        </div>
      )}

      <div className="space-y-2">
        <SortableList
          items={tree?.stages ?? []}
          onReorder={async (ids) => {
            try {
              await reorderEntities({ data: { kind: "stages", parentId: templateId, orderedIds: ids } });
              invalidate();
            } catch (e: any) {
              toast.error(e?.message ?? "Falha ao reordenar etapas.");
              invalidate();
              throw e;
            }
          }}
        >
          {(st: any, sIdx: number) => {
            const arr = tree?.stages ?? [];
            return (
              <SortableRow id={st.id} className="mb-2 flex items-start gap-1 rounded-lg border border-black/[0.06] bg-[var(--gray-soft)]/30 p-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <span className="grid h-6 w-6 place-items-center rounded-md bg-[var(--navy)]/10 text-[10px] font-bold text-[var(--navy)]">
                      {sIdx + 1}
                    </span>
                    <div className="min-w-0 flex-1 truncate text-xs font-semibold text-[var(--navy)]">
                      {st.icon} {st.title}
                    </div>
                    <IconBtn label="Subir" onClick={() => mvStage({ data: { id: st.id, direction: "up" } }).then(invalidate)} disabled={sIdx === 0}>
                      <ArrowUp size={11} />
                    </IconBtn>
                    <IconBtn label="Descer" onClick={() => mvStage({ data: { id: st.id, direction: "down" } }).then(invalidate)} disabled={sIdx === arr.length - 1}>
                      <ArrowDown size={11} />
                    </IconBtn>
                    <IconBtn label="Arquivar" onClick={() => removeStage(st.id)}>
                      <Trash2 size={11} />
                    </IconBtn>
                  </div>

                  <div className="mt-1 space-y-0.5">
                    <SortableList
                      items={st.missions ?? []}
                      onReorder={async (ids) => {
                        try {
                          await reorderEntities({ data: { kind: "missions", parentId: st.id, orderedIds: ids } });
                          invalidate();
                        } catch (e: any) {
                          toast.error(e?.message ?? "Falha ao reordenar missões.");
                          invalidate();
                          throw e;
                        }
                      }}
                    >
                      {(m: any, mIdx: number) => {
                        const mArr = st.missions ?? [];
                        const active = m.id === selectedMissionId;
                        const missing = m._counts.slides + m._counts.questions === 0 && !m._deliverable;
                        return (
                          <SortableRow id={m.id} className={`group flex items-center gap-1 rounded-md px-1.5 py-1 text-xs ${active ? "bg-[var(--navy)] text-white" : "hover:bg-white"}`}>
                            <button
                              onClick={() => onSelectMission(m.id)}
                              className="min-w-0 flex-1 truncate text-left"
                            >
                              <div className="truncate font-medium">{m.title}</div>
                              <div className={`truncate text-[10px] ${active ? "text-white/70" : "text-[var(--gray-ink)]"}`}>
                                {m._counts.slides}s · {m._counts.questions}p · {m._counts.externals}c
                                {missing && <span className="ml-1 text-[var(--gold)]">•</span>}
                              </div>
                            </button>
                            <IconBtn label="Subir" onClick={() => mvMission({ data: { id: m.id, direction: "up" } }).then(invalidate)} disabled={mIdx === 0} light={active}>
                              <ArrowUp size={10} />
                            </IconBtn>
                            <IconBtn label="Descer" onClick={() => mvMission({ data: { id: m.id, direction: "down" } }).then(invalidate)} disabled={mIdx === mArr.length - 1} light={active}>
                              <ArrowDown size={10} />
                            </IconBtn>
                            <IconBtn label="Arquivar" onClick={() => removeMission(m.id)} light={active}>
                              <Trash2 size={10} />
                            </IconBtn>
                          </SortableRow>
                        );
                      }}
                    </SortableList>

                    <button
                      onClick={() => addMission.mutate(st.id)}
                      disabled={addMission.isPending}
                      className="mt-1 inline-flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-black/15 px-2 py-1 text-[11px] font-medium text-[var(--gray-ink)] hover:border-[var(--navy)]/40 hover:text-[var(--navy)]"
                    >
                      <Plus size={10} /> Missão
                    </button>
                  </div>
                </div>
              </SortableRow>
            );
          }}
        </SortableList>
      </div>
    </aside>
  );
}

function IconBtn({ children, onClick, disabled, label, light }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; label: string; light?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`grid h-6 w-6 place-items-center rounded-md transition disabled:opacity-30 ${
        light ? "text-white/80 hover:bg-white/10" : "text-[var(--gray-ink)] hover:bg-black/[0.06] hover:text-[var(--navy)]"
      }`}
    >
      {children}
    </button>
  );
}

// ==================== TEMPLATE INFO PANEL ====================

function TemplateInfoPanel({ template }: { template: any }) {
  const qc = useQueryClient();
  const save = useServerFn(updateTemplate);
  const [title, setTitle] = useState(template?.title ?? "");
  const [days, setDays] = useState(template?.estimated_days ?? "");

  useEffect(() => {
    setTitle(template?.title ?? "");
    setDays(template?.estimated_days ?? "");
  }, [template?.id]);

  const status = useAutosave(async () => {
    if (!template?.id) return;
    await save({ data: {
      id: template.id,
      title,
      estimated_days: days === "" ? null : Number(days),
    } });
    qc.invalidateQueries({ queryKey: ["admin.jornada.tree", template.id] });
    qc.invalidateQueries({ queryKey: ["admin.jornadas.list"] });
  }, [title, days, template?.id]);

  if (!template) return <DataCard><div className="p-8 text-sm text-[var(--gray-ink)]">Carregando…</div></DataCard>;

  return (
    <DataCard>
      <div className="border-b border-black/[0.06] px-6 py-4">
        <div className="flex items-center gap-2 text-xs text-[var(--gray-ink)]">
          <Info size={12} /> Selecione uma missão à esquerda ou edite a jornada.
          <SaveStatus status={status} />
        </div>
      </div>
      <div className="space-y-4 p-6">
        <Field label="Título da jornada">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Duração estimada (dias)">
          <input type="number" min={0} value={days} onChange={(e) => setDays(e.target.value)} className={inputCls} />
        </Field>
      </div>
    </DataCard>
  );
}

// ==================== MISSION EDITOR PANEL ====================

function MissionEditorPanel({
  missionId, tab, setTab, onDeleted, refreshTree,
}: {
  missionId: string;
  tab: Tab;
  setTab: (t: Tab) => void;
  onDeleted: () => void;
  refreshTree: () => void;
}) {
  const loadMission = useServerFn(getMissionEditor);
  const validate = useServerFn(validateMission);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin.mission.editor", missionId],
    queryFn: () => loadMission({ data: { missionId } }),
  });

  const validation = useQuery({
    queryKey: ["admin.mission.validate", missionId],
    queryFn: () => validate({ data: { missionId } }),
    refetchOnWindowFocus: false,
  });

  if (isLoading) return <DataCard><div className="p-8 text-sm text-[var(--gray-ink)]">Carregando missão…</div></DataCard>;
  if (!data) return null;

  const mission = data.mission;
  const tabs: { key: Tab; label: string; icon: any; count?: number; warn?: boolean }[] = [
    { key: "info", label: "Informações", icon: Info },
    { key: "slides", label: "Slides", icon: FileText, count: data.slides.length },
    { key: "questions", label: "Perguntas", icon: HelpCircle, count: data.questions.length },
    { key: "deliverable", label: "Entregável", icon: Package, warn: !data.deliverable },
    { key: "externals", label: "Checklist externo", icon: ListChecks, count: data.externals.length },
    { key: "resources", label: "Materiais", icon: Link2, count: data.resources.length },
  ];

  return (
    <div className="space-y-4">
      {/* Mission header + tabs */}
      <div className="rounded-2xl border border-black/[0.06] bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/[0.06] px-5 py-3">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Missão</div>
            <div className="truncate font-display text-lg font-extrabold text-[var(--navy)]">{mission.title}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[11px] text-[var(--gray-ink)]">
              {data.usage.states} em uso · {data.usage.progress} concluído(s)
            </div>
            <Link
              to="/admin/conteudos/jornadas/preview/$missionId"
              params={{ missionId }}
              target="_blank"
              className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2.5 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-[var(--gray-soft)]"
            >
              <Eye size={12} /> Preview responsivo
            </Link>
          </div>
        </div>
        <nav className="flex flex-wrap gap-1 p-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                tab === t.key
                  ? "bg-[var(--navy)] text-white"
                  : "text-[var(--gray-ink)] hover:bg-[var(--gray-soft)] hover:text-[var(--navy)]"
              }`}
            >
              <t.icon size={12} /> {t.label}
              {t.count !== undefined && (
                <span className={`rounded-full px-1.5 text-[10px] ${tab === t.key ? "bg-white/20" : "bg-black/[0.06]"}`}>{t.count}</span>
              )}
              {t.warn && <AlertTriangle size={11} className="text-[var(--gold)]" />}
            </button>
          ))}
        </nav>
      </div>

      {/* Validation summary */}
      {(validation.data?.issues.length ?? 0) > 0 && (
        <ValidationBanner issues={validation.data!.issues} onRefresh={() => validation.refetch()} />
      )}

      {tab === "info" && <MissionInfoForm mission={mission} siblings={data.siblings} onSaved={() => { refetch(); refreshTree(); validation.refetch(); }} onDelete={onDeleted} />}
      {tab === "slides" && <SlidesEditor missionId={missionId} slides={data.slides} onChange={() => { refetch(); refreshTree(); validation.refetch(); }} />}
      {tab === "questions" && <QuestionsEditor missionId={missionId} questions={data.questions} onChange={() => { refetch(); validation.refetch(); }} />}
      {tab === "deliverable" && <DeliverableEditor missionId={missionId} deliverable={data.deliverable} onChange={() => { refetch(); refreshTree(); validation.refetch(); }} />}
      {tab === "externals" && <ExternalsEditor missionId={missionId} externals={data.externals} onChange={() => { refetch(); validation.refetch(); }} />}
      {tab === "resources" && <ResourcesEditor missionId={missionId} resources={data.resources} slides={data.slides} questions={data.questions} onChange={() => { refetch(); }} />}
    </div>
  );
}

// ==================== MISSION INFO ====================

function MissionInfoForm({ mission, siblings, onSaved, onDelete }: { mission: any; siblings: any[]; onSaved: () => void; onDelete: () => void }) {
  const save = useServerFn(upsertMission);
  const del = useServerFn(deleteMission);
  const [form, setForm] = useState(mission);
  useEffect(() => setForm(mission), [mission.id]);

  const status = useAutosave(async () => {
    await save({ data: {
      id: form.id,
      stage_id: form.stage_id,
      title: form.title,
      objective: form.objective,
      description: form.description,
      expected_result: form.expected_result,
      estimated_minutes: form.estimated_minutes ? Number(form.estimated_minutes) : null,
      difficulty: form.difficulty,
      depends_on_mission_id: form.depends_on_mission_id || null,
    } });
    onSaved();
  }, [form]);

  const remove = async () => {
    if (!confirm("Arquivar esta missão? Ela some de novas publicações, mas o histórico continua intacto.")) return;
    await del({ data: { id: form.id } });
    toast.success("Missão arquivada.");
    onDelete();
  };


  return (
    <DataCard>
      <PanelHeader title="Informações" status={status} />
      <div className="space-y-4 p-5">
        <Field label="Título *"><input value={form.title ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, title: e.target.value }))} className={inputCls} /></Field>
        <Field label="Objetivo"><textarea rows={2} value={form.objective ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, objective: e.target.value }))} className={inputCls} /></Field>
        <Field label="Descrição"><textarea rows={3} value={form.description ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, description: e.target.value }))} className={inputCls} /></Field>
        <Field label="Resultado esperado"><textarea rows={2} value={form.expected_result ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, expected_result: e.target.value }))} className={inputCls} /></Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Tempo estimado (min)"><input type="number" min={0} value={form.estimated_minutes ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, estimated_minutes: e.target.value }))} className={inputCls} /></Field>
          <Field label="Dificuldade">
            <select value={form.difficulty ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, difficulty: e.target.value }))} className={inputCls}>
              <option value="">—</option>
              <option value="baixa">Baixa</option>
              <option value="media">Média</option>
              <option value="alta">Alta</option>
            </select>
          </Field>
          <Field label="Depende de (próxima)">
            <select value={form.depends_on_mission_id ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, depends_on_mission_id: e.target.value }))} className={inputCls}>
              <option value="">— nenhuma —</option>
              {siblings.map((s: any) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </Field>
        </div>
      </div>
      <div className="flex items-center justify-end border-t border-black/[0.06] px-5 py-3">
        <button onClick={remove} className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100">
          <Trash2 size={12} /> Excluir missão
        </button>
      </div>
    </DataCard>
  );
}

// ==================== SLIDES ====================

function SlidesEditor({ missionId, slides, onChange }: { missionId: string; slides: any[]; onChange: () => void }) {
  const save = useServerFn(upsertSlide);
  const del = useServerFn(deleteSlide);
  const mv = useServerFn(moveSlide);

  const [selectedId, setSelectedId] = useState<string | undefined>(slides[0]?.id);
  useEffect(() => {
    if (selectedId && !slides.find(s => s.id === selectedId)) setSelectedId(slides[0]?.id);
    if (!selectedId && slides[0]) setSelectedId(slides[0].id);
  }, [slides, selectedId]);

  const current = slides.find(s => s.id === selectedId);

  const addSlide = async () => {
    const r = await save({ data: { mission_id: missionId, title: `Slide ${slides.length + 1}` } });
    onChange();
    setSelectedId((r as any).id);
  };

  const removeSlide = async (id: string) => {
    if (!confirm("Excluir este slide?")) return;
    await del({ data: { id } });
    onChange();
    toast.success("Slide removido.");
  };

  return (
    <DataCard>
      <PanelHeader title="Slides — Momento Entender" actions={
        <button onClick={addSlide} className={smallPrimaryBtn}><Plus size={12}/> Slide</button>
      } />
      <div className="grid gap-0 md:grid-cols-[260px_1fr]">
        <div className="border-b border-black/[0.06] p-2 md:border-b-0 md:border-r">
          {slides.length === 0 && <div className="p-3 text-xs text-[var(--gray-ink)]">Nenhum slide.</div>}
          <SortableList
            items={slides}
            onReorder={async (ids) => {
              try { await reorderEntities({ data: { kind: "slides", parentId: missionId, orderedIds: ids } }); onChange(); }
              catch (e: any) { toast.error(e?.message ?? "Falha ao reordenar."); onChange(); throw e; }
            }}
          >
            {(s: any, i: number) => (
              <SortableRow id={s.id} className={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-xs ${selectedId === s.id ? "bg-[var(--navy)] text-white" : "hover:bg-[var(--gray-soft)]"}`}>
                <span className="w-5 text-[10px] font-bold opacity-70">{i + 1}</span>
                <button onClick={() => setSelectedId(s.id)} className="min-w-0 flex-1 truncate text-left">{s.title || "(sem título)"}</button>
                <IconBtn label="Subir" light={selectedId === s.id} disabled={i === 0} onClick={() => mv({ data: { id: s.id, direction: "up" } }).then(onChange)}><ArrowUp size={10}/></IconBtn>
                <IconBtn label="Descer" light={selectedId === s.id} disabled={i === slides.length - 1} onClick={() => mv({ data: { id: s.id, direction: "down" } }).then(onChange)}><ArrowDown size={10}/></IconBtn>
                <IconBtn label="Arquivar" light={selectedId === s.id} onClick={() => removeSlide(s.id)}><Trash2 size={10}/></IconBtn>
              </SortableRow>
            )}
          </SortableList>
        </div>
        <div className="p-5">
          {current ? <SlideForm key={current.id} slide={current} missionId={missionId} onSaved={onChange} /> : <div className="text-sm text-[var(--gray-ink)]">Selecione um slide.</div>}
        </div>
      </div>
    </DataCard>
  );
}

function SlideForm({ slide, missionId, onSaved }: { slide: any; missionId: string; onSaved: () => void }) {
  const save = useServerFn(upsertSlide);
  const [form, setForm] = useState(slide);
  useEffect(() => setForm(slide), [slide.id]);

  const status = useAutosave(async () => {
    await save({ data: {
      id: form.id, mission_id: missionId,
      title: form.title, body: form.body ?? null,
      example: form.example ?? null, icon: form.icon ?? null,
      image_url: form.image_url || null,
    } });
    onSaved();
  }, [form]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end"><SaveStatus status={status} /></div>
      <div className="grid gap-4 sm:grid-cols-[80px_1fr]">
        <Field label="Ícone"><input value={form.icon ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, icon: e.target.value }))} className={inputCls} placeholder="🎯" /></Field>
        <Field label="Título *"><input value={form.title ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, title: e.target.value }))} className={inputCls} /></Field>
      </div>
      <Field label="Corpo (texto principal)"><textarea rows={5} value={form.body ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, body: e.target.value }))} className={inputCls} /></Field>
      <Field label="Exemplo"><textarea rows={2} value={form.example ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, example: e.target.value }))} className={inputCls} /></Field>
      <Field label="Imagem (URL)"><input value={form.image_url ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, image_url: e.target.value }))} placeholder="https://..." className={inputCls} /></Field>
    </div>
  );
}

// ==================== QUESTIONS ====================

function QuestionsEditor({ missionId, questions, onChange }: { missionId: string; questions: any[]; onChange: () => void }) {
  const save = useServerFn(upsertQuestion);
  const del = useServerFn(deleteQuestion);
  const mv = useServerFn(moveQuestion);
  const [openId, setOpenId] = useState<string | null>(questions[0]?.id ?? null);

  const addQuestion = async () => {
    try {
      const r = await save({ data: {
        mission_id: missionId,
        key: `pergunta_${questions.length + 1}`,
        prompt: "Nova pergunta",
        input_type: "single",
        options: [],
        required: true,
        allow_other: false,
      }});
      onChange();
      setOpenId((r as any).id);
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  return (
    <DataCard>
      <PanelHeader title="Perguntas — Momento Decidir" actions={
        <button onClick={addQuestion} className={smallPrimaryBtn}><Plus size={12}/> Pergunta</button>
      } />
      <div className="divide-y divide-black/[0.06]">
        {questions.length === 0 && <div className="p-6 text-sm text-[var(--gray-ink)]">Sem perguntas.</div>}
        <SortableList
          items={questions}
          onReorder={async (ids) => {
            try { await reorderEntities({ data: { kind: "questions", parentId: missionId, orderedIds: ids } }); onChange(); }
            catch (e: any) { toast.error(e?.message ?? "Falha ao reordenar."); onChange(); throw e; }
          }}
        >
          {(q: any, i: number) => (
            <SortableRow id={q.id}>
              <div className="flex items-center gap-2 px-5 py-3">
                <span className="grid h-6 w-6 place-items-center rounded-md bg-[var(--navy)]/10 text-[10px] font-bold text-[var(--navy)]">{i + 1}</span>
                <button onClick={() => setOpenId(openId === q.id ? null : q.id)} className="min-w-0 flex-1 text-left">
                  <div className="truncate text-sm font-semibold text-[var(--navy)]">{q.prompt}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] text-[var(--gray-ink)]">
                    <code className="rounded bg-black/[0.05] px-1">{q.key}</code>
                    <span>{q.input_type}</span>
                    {q.required && <StatusPill tone="navy">obrigatória</StatusPill>}
                    {q.condition && <StatusPill tone="gold">condicional</StatusPill>}
                  </div>
                </button>
                <IconBtn label="Subir" disabled={i === 0} onClick={() => mv({ data: { id: q.id, direction: "up" } }).then(onChange)}><ArrowUp size={10}/></IconBtn>
                <IconBtn label="Descer" disabled={i === questions.length - 1} onClick={() => mv({ data: { id: q.id, direction: "down" } }).then(onChange)}><ArrowDown size={10}/></IconBtn>
                <IconBtn label="Arquivar" onClick={async () => { if (confirm("Arquivar esta pergunta?")) { await del({ data: { id: q.id } }); onChange(); }}}><Trash2 size={10}/></IconBtn>
                <ChevronRight size={12} className={`transition ${openId === q.id ? "rotate-90" : ""}`}/>
              </div>
              {openId === q.id && <QuestionForm question={q} allQuestions={questions} missionId={missionId} onSaved={onChange} />}
            </SortableRow>
          )}
        </SortableList>
      </div>
    </DataCard>
  );
}

function QuestionForm({ question, allQuestions, missionId, onSaved }: { question: any; allQuestions: any[]; missionId: string; onSaved: () => void }) {
  const save = useServerFn(upsertQuestion);
  const [form, setForm] = useState(() => ({
    ...question,
    options: Array.isArray(question.options) ? question.options : [],
    condition: question.condition ?? null,
  }));
  useEffect(() => setForm({
    ...question,
    options: Array.isArray(question.options) ? question.options : [],
    condition: question.condition ?? null,
  }), [question.id]);

  const status = useAutosave(async () => {
    try {
      await save({ data: {
        id: form.id, mission_id: missionId,
        key: form.key, prompt: form.prompt,
        help_text: form.help_text ?? null,
        input_type: form.input_type,
        options: form.options,
        required: !!form.required,
        allow_other: !!form.allow_other,
        condition: form.condition,
      }});
      onSaved();
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao salvar pergunta.");
      throw e;
    }
  }, [form]);

  const needsOptions = form.input_type === "single" || form.input_type === "multi";
  const otherQuestions = allQuestions.filter((q: any) => q.id !== form.id);

  return (
    <div className="space-y-4 border-t border-black/[0.06] bg-[var(--gray-soft)]/30 p-5">
      <div className="flex items-center justify-end"><SaveStatus status={status} /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Chave (identificador) *"><input value={form.key} onChange={(e) => setForm((f: any) => ({ ...f, key: e.target.value }))} className={inputCls} /></Field>
        <Field label="Tipo *">
          <select value={form.input_type} onChange={(e) => setForm((f: any) => ({ ...f, input_type: e.target.value }))} className={inputCls}>
            <option value="single">Escolha única</option>
            <option value="multi">Múltipla escolha</option>
            <option value="text">Texto curto</option>
            <option value="textarea">Texto longo</option>
            <option value="number">Número</option>
          </select>
        </Field>
      </div>
      <Field label="Enunciado *"><textarea rows={2} value={form.prompt} onChange={(e) => setForm((f: any) => ({ ...f, prompt: e.target.value }))} className={inputCls} /></Field>
      <Field label="Texto de ajuda"><input value={form.help_text ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, help_text: e.target.value }))} className={inputCls} /></Field>

      <div className="flex flex-wrap gap-4">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.required} onChange={(e) => setForm((f: any) => ({ ...f, required: e.target.checked }))} />
          Obrigatória
        </label>
        {needsOptions && (
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.allow_other} onChange={(e) => setForm((f: any) => ({ ...f, allow_other: e.target.checked }))} />
            Permitir "Outro"
          </label>
        )}
      </div>

      {needsOptions && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-[var(--navy)]">Opções</span>
            <button onClick={() => setForm((f: any) => ({ ...f, options: [...f.options, { value: `opt_${f.options.length + 1}`, label: "Nova opção", description: "" }] }))} className={smallPrimaryBtn}>
              <Plus size={11}/> Opção
            </button>
          </div>
          <div className="space-y-2">
            {form.options.map((o: any, idx: number) => (
              <div key={idx} className="grid gap-2 rounded-md border border-black/[0.08] bg-white p-2 sm:grid-cols-[120px_1fr_1fr_auto]">
                <input value={o.value} onChange={(e) => setForm((f: any) => { const opts = [...f.options]; opts[idx] = { ...opts[idx], value: e.target.value }; return { ...f, options: opts }; })} placeholder="valor" className={inputCls}/>
                <input value={o.label} onChange={(e) => setForm((f: any) => { const opts = [...f.options]; opts[idx] = { ...opts[idx], label: e.target.value }; return { ...f, options: opts }; })} placeholder="rótulo" className={inputCls}/>
                <input value={o.description ?? ""} onChange={(e) => setForm((f: any) => { const opts = [...f.options]; opts[idx] = { ...opts[idx], description: e.target.value }; return { ...f, options: opts }; })} placeholder="descrição" className={inputCls}/>
                <button onClick={() => setForm((f: any) => ({ ...f, options: f.options.filter((_: any, i: number) => i !== idx) }))} className="grid h-9 w-9 place-items-center rounded-md text-[var(--gray-ink)] hover:bg-red-50 hover:text-red-600"><Trash2 size={12}/></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConditionEditor
        condition={form.condition}
        otherQuestions={otherQuestions}
        onChange={(c) => setForm((f: any) => ({ ...f, condition: c }))}
      />
    </div>
  );
}

function ConditionEditor({ condition, otherQuestions, onChange }: { condition: any; otherQuestions: any[]; onChange: (c: any) => void }) {
  const on = !!condition;
  const c = condition ?? { when: otherQuestions[0]?.key ?? "", equals: "" };
  const source = otherQuestions.find((q: any) => q.key === c.when);
  const opts: any[] = Array.isArray(source?.options) ? source.options : [];

  return (
    <div className="rounded-lg border border-dashed border-black/15 p-3">
      <label className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--navy)]">
        <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked ? c : null)} />
        Exibir apenas se…
      </label>
      {on && (
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto_1fr]">
          <select value={c.when} onChange={(e) => onChange({ ...c, when: e.target.value })} className={inputCls}>
            {otherQuestions.map((q: any) => <option key={q.id} value={q.key}>{q.key} — {q.prompt.slice(0, 40)}</option>)}
          </select>
          <div className="grid place-items-center text-xs font-semibold text-[var(--gray-ink)]">for igual a</div>
          {opts.length > 0 ? (
            <select value={c.equals ?? ""} onChange={(e) => onChange({ ...c, equals: e.target.value })} className={inputCls}>
              <option value="">— selecione —</option>
              {opts.map((o: any) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : (
            <input value={c.equals ?? ""} onChange={(e) => onChange({ ...c, equals: e.target.value })} className={inputCls} placeholder="valor esperado"/>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== DELIVERABLE ====================

function DeliverableEditor({ missionId, deliverable, onChange }: { missionId: string; deliverable: any; onChange: () => void }) {
  const save = useServerFn(upsertDeliverable);
  const del = useServerFn(deleteDeliverable);
  const [form, setForm] = useState<any>(deliverable ?? {
    kind: "configuration",
    title: "Configuração do modelo",
    description: "",
    target_field: WORKSPACE_FIELDS[0],
    template: {},
  });
  useEffect(() => setForm(deliverable ?? {
    kind: "configuration",
    title: "Configuração do modelo",
    description: "",
    target_field: WORKSPACE_FIELDS[0],
    template: {},
  }), [deliverable?.id]);

  const [templateStr, setTemplateStr] = useState(() => JSON.stringify(form.template ?? {}, null, 2));
  const [templateErr, setTemplateErr] = useState<string | null>(null);
  useEffect(() => setTemplateStr(JSON.stringify(form.template ?? {}, null, 2)), [deliverable?.id]);

  const status = useAutosave(async () => {
    let parsed: any = {};
    try { parsed = JSON.parse(templateStr || "{}"); setTemplateErr(null); }
    catch (e: any) { setTemplateErr(e?.message ?? "JSON inválido"); return; }
    try {
      await save({ data: {
        id: form.id, mission_id: missionId,
        kind: form.kind ?? "configuration",
        title: form.title,
        description: form.description ?? null,
        target_field: form.target_field,
        template: parsed,
      }});
      onChange();
    } catch (e: any) {
      toast.error(e?.message ?? "Erro"); throw e;
    }
  }, [form, templateStr]);

  const remove = async () => {
    if (!form.id) return;
    if (!confirm("Excluir o entregável desta missão? Membros que finalizarem depois disso não gravarão nada em Meu Negócio.")) return;
    await del({ data: { id: form.id } });
    onChange();
    toast.success("Entregável removido.");
  };

  return (
    <DataCard>
      <PanelHeader title="Entregável — Momento Finalizar" status={status} actions={
        form.id ? <button onClick={remove} className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-100"><Trash2 size={11}/> Remover</button> : null
      } />
      <div className="space-y-4 p-5">
        <div className="rounded-lg border border-[var(--navy)]/10 bg-[var(--navy)]/[0.04] p-3 text-xs text-[var(--navy)]">
          O entregável é o que a missão grava em <strong>Meu Negócio</strong> quando o membro
          clica em Finalizar. Ele é mesclado com as respostas da missão no campo escolhido abaixo.
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Título *"><input value={form.title ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, title: e.target.value }))} className={inputCls} /></Field>
          <Field label="Destino em Meu Negócio *">
            <select value={form.target_field} onChange={(e) => setForm((f: any) => ({ ...f, target_field: e.target.value }))} className={inputCls}>
              {WORKSPACE_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Descrição"><textarea rows={2} value={form.description ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, description: e.target.value }))} className={inputCls} /></Field>
        <Field label="Template (JSON — chaves iniciais do bloco em Meu Negócio)">
          <textarea rows={8} value={templateStr} onChange={(e) => setTemplateStr(e.target.value)} className={`${inputCls} font-mono text-xs`} />
          {templateErr && <p className="mt-1 text-xs text-red-600">{templateErr}</p>}
        </Field>
      </div>
    </DataCard>
  );
}

// ==================== EXTERNALS ====================

function ExternalsEditor({ missionId, externals, onChange }: { missionId: string; externals: any[]; onChange: () => void }) {
  const save = useServerFn(upsertExternalTask);
  const del = useServerFn(deleteExternalTask);
  const mv = useServerFn(moveExternalTask);
  const [openId, setOpenId] = useState<string | null>(externals[0]?.id ?? null);

  const add = async () => {
    try {
      const r = await save({ data: {
        mission_id: missionId,
        key: `acao_${externals.length + 1}`,
        title: "Nova ação externa",
      }});
      onChange();
      setOpenId((r as any).id);
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  return (
    <DataCard>
      <PanelHeader title="Checklist externo — ações fora da plataforma" actions={
        <button onClick={add} className={smallPrimaryBtn}><Plus size={12}/> Ação</button>
      } />
      <div className="divide-y divide-black/[0.06]">
        {externals.length === 0 && <div className="p-6 text-sm text-[var(--gray-ink)]">Sem ações externas.</div>}
        <SortableList
          items={externals}
          onReorder={async (ids) => {
            try { await reorderEntities({ data: { kind: "externals", parentId: missionId, orderedIds: ids } }); onChange(); }
            catch (e: any) { toast.error(e?.message ?? "Falha ao reordenar."); onChange(); throw e; }
          }}
        >
          {(e: any, i: number) => (
            <SortableRow id={e.id}>
              <div className="flex items-center gap-2 px-5 py-3">
                <span className="grid h-6 w-6 place-items-center rounded-md bg-[var(--navy)]/10 text-[10px] font-bold text-[var(--navy)]">{i + 1}</span>
                <button onClick={() => setOpenId(openId === e.id ? null : e.id)} className="min-w-0 flex-1 text-left">
                  <div className="truncate text-sm font-semibold text-[var(--navy)]">{e.title}</div>
                  <div className="text-[10px] text-[var(--gray-ink)]"><code className="rounded bg-black/[0.05] px-1">{e.key}</code></div>
                </button>
                <IconBtn label="Subir" disabled={i === 0} onClick={() => mv({ data: { id: e.id, direction: "up" } }).then(onChange)}><ArrowUp size={10}/></IconBtn>
                <IconBtn label="Descer" disabled={i === externals.length - 1} onClick={() => mv({ data: { id: e.id, direction: "down" } }).then(onChange)}><ArrowDown size={10}/></IconBtn>
                <IconBtn label="Arquivar" onClick={async () => { if (confirm("Arquivar esta ação?")) { await del({ data: { id: e.id } }); onChange(); }}}><Trash2 size={10}/></IconBtn>
              </div>
              {openId === e.id && <ExternalForm ext={e} missionId={missionId} onSaved={onChange} />}
            </SortableRow>
          )}
        </SortableList>
      </div>
    </DataCard>
  );
}

function ExternalForm({ ext, missionId, onSaved }: { ext: any; missionId: string; onSaved: () => void }) {
  const save = useServerFn(upsertExternalTask);
  const [form, setForm] = useState(ext);
  useEffect(() => setForm(ext), [ext.id]);

  const status = useAutosave(async () => {
    try {
      await save({ data: {
        id: form.id, mission_id: missionId,
        key: form.key, title: form.title,
        description: form.description ?? null,
        url: form.url || null,
      }});
      onSaved();
    } catch (e: any) { toast.error(e?.message ?? "Erro"); throw e; }
  }, [form]);

  return (
    <div className="space-y-4 border-t border-black/[0.06] bg-[var(--gray-soft)]/30 p-5">
      <div className="flex items-center justify-end"><SaveStatus status={status} /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Chave *"><input value={form.key} onChange={(e) => setForm((f: any) => ({ ...f, key: e.target.value }))} className={inputCls} /></Field>
        <Field label="Título *"><input value={form.title} onChange={(e) => setForm((f: any) => ({ ...f, title: e.target.value }))} className={inputCls} /></Field>
      </div>
      <Field label="Descrição"><textarea rows={2} value={form.description ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, description: e.target.value }))} className={inputCls} /></Field>
      <Field label="URL (opcional)"><input value={form.url ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, url: e.target.value }))} placeholder="https://…" className={inputCls} /></Field>
    </div>
  );
}

// ==================== RESOURCES ====================

function ResourcesEditor({ missionId, resources, slides, questions, onChange }: { missionId: string; resources: any[]; slides: any[]; questions: any[]; onChange: () => void }) {
  const save = useServerFn(upsertResource);
  const del = useServerFn(deleteResource);
  const mv = useServerFn(moveResource);
  const [openId, setOpenId] = useState<string | null>(resources[0]?.id ?? null);

  const add = async () => {
    const r = await save({ data: {
      mission_id: missionId,
      anchor_type: "mission",
      title: "Novo material",
      kind: "link",
    }});
    onChange();
    setOpenId((r as any).id);
  };

  return (
    <DataCard>
      <PanelHeader title="Materiais contextuais" actions={
        <button onClick={add} className={smallPrimaryBtn}><Plus size={12}/> Material</button>
      } />
      <div className="divide-y divide-black/[0.06]">
        {resources.length === 0 && <div className="p-6 text-sm text-[var(--gray-ink)]">Sem materiais.</div>}
        <SortableList
          items={resources}
          onReorder={async (ids) => {
            try { await reorderEntities({ data: { kind: "resources", parentId: missionId, orderedIds: ids } }); onChange(); }
            catch (e: any) { toast.error(e?.message ?? "Falha ao reordenar."); onChange(); throw e; }
          }}
        >
          {(r: any, i: number) => (
            <SortableRow id={r.id}>
              <div className="flex items-center gap-2 px-5 py-3">
                <button onClick={() => setOpenId(openId === r.id ? null : r.id)} className="min-w-0 flex-1 text-left">
                  <div className="truncate text-sm font-semibold text-[var(--navy)]">{r.title}</div>
                  <div className="text-[10px] text-[var(--gray-ink)]">{r.anchor_type} · {r.kind}</div>
                </button>
                <IconBtn label="Subir" disabled={i === 0} onClick={() => mv({ data: { id: r.id, direction: "up" } }).then(onChange)}><ArrowUp size={10}/></IconBtn>
                <IconBtn label="Descer" disabled={i === resources.length - 1} onClick={() => mv({ data: { id: r.id, direction: "down" } }).then(onChange)}><ArrowDown size={10}/></IconBtn>
                <IconBtn label="Arquivar" onClick={async () => { if (confirm("Arquivar este material?")) { await del({ data: { id: r.id } }); onChange(); }}}><Trash2 size={10}/></IconBtn>
              </div>
              {openId === r.id && <ResourceForm resource={r} missionId={missionId} slides={slides} questions={questions} onSaved={onChange} />}
            </SortableRow>
          )}
        </SortableList>
      </div>
    </DataCard>
  );
}

function ResourceForm({ resource, missionId, slides, questions, onSaved }: { resource: any; missionId: string; slides: any[]; questions: any[]; onSaved: () => void }) {
  const save = useServerFn(upsertResource);
  const [form, setForm] = useState(resource);
  useEffect(() => setForm(resource), [resource.id]);

  const status = useAutosave(async () => {
    try {
      await save({ data: {
        id: form.id, mission_id: missionId,
        anchor_type: form.anchor_type,
        anchor_id: form.anchor_type === "slide" ? (form.anchor_id || null) : null,
        anchor_key: form.anchor_type === "question" ? (form.anchor_key || null) : null,
        title: form.title,
        url: form.url || null,
        kind: form.kind,
        description: form.description ?? null,
      }});
      onSaved();
    } catch (e: any) { toast.error(e?.message ?? "Erro"); throw e; }
  }, [form]);

  return (
    <div className="space-y-4 border-t border-black/[0.06] bg-[var(--gray-soft)]/30 p-5">
      <div className="flex items-center justify-end"><SaveStatus status={status} /></div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Vínculo *">
          <select value={form.anchor_type} onChange={(e) => setForm((f: any) => ({ ...f, anchor_type: e.target.value }))} className={inputCls}>
            <option value="mission">Missão inteira</option>
            <option value="slide">Slide específico</option>
            <option value="question">Pergunta específica</option>
            <option value="step">Etapa (entender/decidir/construir/finalizar)</option>
          </select>
        </Field>
        {form.anchor_type === "slide" && (
          <Field label="Slide">
            <select value={form.anchor_id ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, anchor_id: e.target.value }))} className={inputCls}>
              <option value="">— selecione —</option>
              {slides.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </Field>
        )}
        {form.anchor_type === "question" && (
          <Field label="Pergunta (chave)">
            <select value={form.anchor_key ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, anchor_key: e.target.value }))} className={inputCls}>
              <option value="">— selecione —</option>
              {questions.map((q) => <option key={q.id} value={q.key}>{q.key}</option>)}
            </select>
          </Field>
        )}
        {form.anchor_type === "step" && (
          <Field label="Momento (chave)">
            <select value={form.anchor_key ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, anchor_key: e.target.value }))} className={inputCls}>
              <option value="entender">entender</option>
              <option value="decidir">decidir</option>
              <option value="construir">construir</option>
              <option value="finalizar">finalizar</option>
            </select>
          </Field>
        )}
        <Field label="Tipo">
          <select value={form.kind} onChange={(e) => setForm((f: any) => ({ ...f, kind: e.target.value }))} className={inputCls}>
            <option value="link">Link</option>
            <option value="video">Vídeo</option>
            <option value="doc">Documento</option>
            <option value="download">Download</option>
            <option value="template">Template</option>
          </select>
        </Field>
      </div>
      <Field label="Título *"><input value={form.title} onChange={(e) => setForm((f: any) => ({ ...f, title: e.target.value }))} className={inputCls} /></Field>
      <Field label="URL"><input value={form.url ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, url: e.target.value }))} placeholder="https://…" className={inputCls} /></Field>
      <Field label="Descrição"><textarea rows={2} value={form.description ?? ""} onChange={(e) => setForm((f: any) => ({ ...f, description: e.target.value }))} className={inputCls} /></Field>
    </div>
  );
}

// ==================== VALIDATION BANNER ====================

function ValidationBanner({ issues, onRefresh }: { issues: any[]; onRefresh: () => void }) {
  const errors = issues.filter((i) => i.severity === "error");
  const warns = issues.filter((i) => i.severity === "warning");
  return (
    <div className={`rounded-2xl border p-4 ${errors.length ? "border-red-200 bg-red-50" : "border-[var(--gold)]/30 bg-[var(--gold)]/[0.06]"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle size={14} className={errors.length ? "text-red-600" : "text-[var(--gold)]"}/>
          {errors.length ? `${errors.length} erro(s) crítico(s)` : `${warns.length} alerta(s)`}
          {warns.length > 0 && errors.length > 0 && <span className="text-[var(--gray-ink)]"> · {warns.length} alerta(s)</span>}
        </div>
        <button onClick={onRefresh} className="inline-flex items-center gap-1 text-xs text-[var(--gray-ink)] hover:text-[var(--navy)]"><RefreshCw size={11}/> Revalidar</button>
      </div>
      <ul className="mt-2 space-y-1 text-xs">
        {issues.map((i, idx) => (
          <li key={idx} className={i.severity === "error" ? "text-red-700" : "text-[var(--gold)]"}>
            <strong>{i.severity === "error" ? "Erro:" : "Alerta:"}</strong> {i.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ==================== SHARED PRIMITIVES ====================

const inputCls =
  "w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm text-[var(--navy)] outline-none transition focus:border-[var(--navy)]/40";
const smallPrimaryBtn =
  "inline-flex items-center gap-1 rounded-md bg-[var(--navy)] px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{label}</span>
      {children}
    </label>
  );
}

function PanelHeader({ title, status, actions }: { title: string; status?: SaveStatusValue; actions?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-black/[0.06] px-5 py-3">
      <div className="text-sm font-semibold text-[var(--navy)]">{title}</div>
      <div className="flex items-center gap-2">
        {status && <SaveStatus status={status} />}
        {actions}
      </div>
    </div>
  );
}

type SaveStatusValue = "idle" | "unsaved" | "saving" | "saved" | "error";

function SaveStatus({ status }: { status: SaveStatusValue }) {
  const label = {
    idle: "",
    unsaved: "Alterações não salvas",
    saving: "Salvando…",
    saved: "Salvo",
    error: "Erro ao salvar",
  }[status];
  if (!label) return null;
  const color = status === "error" ? "text-red-600" : status === "unsaved" ? "text-[var(--gold)]" : status === "saved" ? "text-[var(--green)]" : "text-[var(--gray-ink)]";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${color}`}>
      {status === "saving" && <Loader2 size={11} className="animate-spin"/>}
      {status === "saved" && <CheckCircle2 size={11}/>}
      {status === "error" && <X size={11}/>}
      {status === "unsaved" && <Save size={11}/>}
      {label}
    </span>
  );
}

// ==================== AUTOSAVE HOOK ====================
// Debounced autosave with status. Skips the very first render so we don't
// re-save server data immediately after loading. Sets beforeunload guard
// whenever there are unsaved changes.
function useAutosave(save: () => Promise<void>, deps: any[]): SaveStatusValue {
  const [status, setStatus] = useState<SaveStatusValue>("idle");
  const firstRun = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setStatus("unsaved");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setStatus("saving");
      try { await saveRef.current(); setStatus("saved"); }
      catch { setStatus("error"); }
    }, 800);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (status === "unsaved" || status === "saving") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [status]);

  return status;
}
